// Service hook (TS-2.5.01, Resource Accessor layer) - the general reference-data mechanism
// (decisions.md D-004: "one general reference-data mechanism" rather than per-vocabulary code) as
// a hook any feature can call for any list code (ReferenceListCodes.cs), mirroring
// useReferenceData's shape (fetch on mount, refresh on push-channel invalidation, non-fatal on
// failure) but generic rather than roadmap-specific. Batch 3 uses it for a project/milestone/note
// form's vocabulary (team, project.status, milestone.completion, note.type, note.status) and for
// the messy-date placeholder vocabulary (date.placeholder) DateField renders as passthrough
// suggestions - never a hardcoded token list.
import { useCallback, useEffect, useState } from "react";
import { configurationApi } from "../api/configuration";
import type { ReferenceList } from "../api/generated/contracts";
import { useInvalidation } from "../realtime/useInvalidation";

export interface UseReferenceListResult {
  readonly list: ReferenceList | null;
  readonly loading: boolean;
}

export function useReferenceList(listCode: string): UseReferenceListResult {
  const [list, setList] = useState<ReferenceList | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    void configurationApi
      .getReferenceList(listCode)
      .then((detail) => setList(detail.list))
      .catch(() => {
        // A failed vocabulary fetch is not fatal to the caller - a form still renders with a
        // free-text field, per D-011: a value the caller cannot yet validate against reference
        // data is still accepted and stored as typed.
      })
      .finally(() => setLoading(false));
  }, [listCode]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh, "ReferenceValue");

  return { list, loading };
}
